import * as React from "react";
import Header from "@components/Header";
import Whatsapp from "@public/assets/icons/whatsapp.svg";

type SupportChannel = {
  title: string;
  description: string;
  contact: string;
  availability: string;
};

const supportChannels: SupportChannel[] = [
  {
    title: "General Support",
    description:
      "Questions about your account, bookings, timelines, or how features work.",
    contact: "hello@inawo.co",
    availability: "Mon-Fri, 9:00 AM - 6:00 PM (WAT)",
  },
  {
    title: "Vendor Support",
    description:
      "Help for venue owners, planners, decorators, photographers, and other vendors.",
    contact: "vendors@inawo.com",
    availability: "Mon-Fri, 9:00 AM - 6:00 PM (WAT)",
  },
  {
    title: "Urgent Event-Day Escalation",
    description:
      "Priority help for issues affecting an active wedding or live booking.",
    contact: "+2347041004669 (WhatsApp)",
    availability: "Daily, 8:00 AM - 10:00 PM (WAT)",
  },
];

const faqItems = [
  {
    question: "How quickly will I get a response?",
    answer:
      "You can expect a response from our support team within 24 hours of sending your message.",
  },
  {
    question: "Can I get onboarding help as a new user?",
    answer:
      "Absolutely! As a new user, you'll receive an onboarding session. Shortly after signing up, our support team will reach out to schedule a call and guide you through the platform.",
  },
  {
    question: "Is Inawo an event planner?",
    answer:
      "No, Inawo is not an event planning service. Inawo provides digital tools and resources designed to make your wedding planning process easier and more organized.",
  },
  {
    question: "Will I have access to my website after my wedding?",
    answer:
      "Yes, you will continue to have access to your event website even after your wedding has taken place.",
  },
  {
    question: "Can I have my own custom domain?",
    answer:
      "Yes, custom domains are available. Simply contact our support team, and they will guide you through the process of setting one up.",
  },
];

const Support: React.FC = () => {
  return (
    <div
      className="bg-gradient-to-b from-inawo-light via-white to-[#f7faf8] text-slate-900"
      data-scroll-section
    >
      <Header />

      <section className="mx-auto max-w-6xl px-4 pb-20 pt-10 sm:px-6 lg:px-8 lg:pt-16">
        <div className="overflow-hidden rounded-3xl border border-emerald-100 bg-white/80 shadow-sm backdrop-blur">
          <div className="bg-inawo-dark px-6 py-10 text-white sm:px-10 lg:px-14">
            <p className="mb-3 inline-flex rounded-full border border-white/30 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]">
              Inawo Support
            </p>
            <h1 className="text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
              We are here to help you plan and celebrate without stress
            </h1>
            <p className="mt-4 max-w-3xl text-sm text-emerald-50 sm:text-base">
              Reach out for product guidance, vendor account assistance, or
              urgent event-day help. Our support team is committed to fast and
              practical resolutions.
            </p>
          </div>

          <div className="grid gap-5 px-6 py-8 sm:px-10 lg:grid-cols-3 lg:px-14 lg:py-10">
            {supportChannels.map((channel) => (
              <article
                key={channel.title}
                className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm"
              >
                <h2 className="text-lg font-semibold text-inawo-dark">
                  {channel.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {channel.description}
                </p>
                <p className="mt-4 text-sm font-medium text-slate-800">
                  Contact:{" "}
                  <span className="text-inawo-base">{channel.contact}</span>
                </p>
                <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                  {channel.availability}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm sm:p-8">
            <h2 className="text-2xl font-semibold text-inawo-dark">
              Before You Contact Us
            </h2>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-slate-700">
              <li>Share your event slug and the account email</li>
              <li>
                Include screenshots (if necessary) or links so we can
                investigate quickly.
              </li>
              <li>For payment issues, include transaction dates.</li>
              <li>
                For urgent requests, add the event date and expected timeline.
              </li>
            </ul>
          </div>

          <div className="rounded-3xl border border-emerald-100 bg-inawo-base p-6 text-white shadow-sm sm:p-8">
            <h2 className="text-2xl font-semibold">Business & Partnerships</h2>
            <p className="mt-4 text-sm leading-relaxed text-emerald-50">
              For enterprise partnerships, media requests, or collaboration with
              Inawo, contact our business team.
            </p>
            <p className="mt-6 text-sm font-medium">hello@inawo.co</p>
            <p className="mt-2 text-xs uppercase tracking-wide text-emerald-100">
              Response within 2 business days
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-emerald-100 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="text-2xl font-semibold text-inawo-dark">
            Frequently Asked Questions
          </h2>
          <div className="mt-6 space-y-4">
            {faqItems.map((item) => (
              <details
                key={item.question}
                className="group rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
              >
                <summary className="cursor-pointer list-none text-sm font-medium text-slate-800">
                  {item.question}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <a
        href="https://wa.me/2347041004669"
        className={
          "fixed bottom-5 right-5 rounded-full shadow-lg transition-transform hover:scale-110"
        }
        aria-label="Chat with Inawo support on WhatsApp"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Whatsapp />
      </a>
    </div>
  );
};

export default Support;
